<template>
    <!-- Single-purpose picker: one question, one list, commit on select.

         Styled to match UserSelectInput / PointSelectTree (the report settings
         primitives): teleported overlay, header with close, search row with a leading
         icon, a persistent selection banner, and a Cancel/footer row. Unlike those it
         is single-select, so choosing a row commits immediately and there is no Done
         button to press.

         When options carry a `group` (zones under their area) the list renders as a
         collapsible two-level tree with indentation, so a site with many areas stays
         navigable. Flat option lists (scaling/alarm profiles) render as a plain list. -->
    <teleport to="body">
        <div v-if="open"
             class="fixed inset-0 z-[60] flex items-center justify-center p-4"
             role="dialog"
             aria-modal="true"
        >
            <div class="absolute inset-0 bg-gray-500 bg-opacity-75" @click="close"></div>

            <div class="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-clip rounded-lg bg-white shadow-xl">
                <!-- Header -->
                <div class="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                    <div class="min-w-0">
                        <h3 class="m-0 p-0 text-left text-base font-semibold text-gray-900">{{ title }}</h3>
                        <p v-if="subtitle" class="m-0 mt-0.5 truncate text-sm text-gray-500">{{ subtitle }}</p>
                    </div>

                    <button type="button"
                            class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            @click="close">
                        <span class="sr-only">Close</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>

                <!-- Search, plus expand/collapse when the list is a tree. -->
                <div class="flex items-center gap-2 border-b border-gray-200 px-5 py-3">
                    <div class="relative flex-1">
                        <svg class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                        <input ref="search"
                               v-model="query"
                               type="text"
                               :placeholder="placeholder"
                               class="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                               @keydown.down.prevent="move(1)"
                               @keydown.up.prevent="move(-1)"
                               @keydown.enter.prevent="commitHighlighted"
                               @keydown.esc.prevent="close"
                        >
                    </div>

                    <button v-if="grouped"
                            type="button"
                            class="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            @click="toggleAllExpanded">
                        {{ allExpanded ? 'Collapse all' : 'Expand all' }}
                    </button>
                </div>

                <!-- Current value. Hidden entirely for a bulk apply: the selected points may hold
                     different values, so there is no single "current" to state and claiming
                     "nothing selected" would be wrong. -->
                <div v-if="showCurrent"
                     class="flex h-11 shrink-0 items-center justify-between gap-3 border-b border-gray-100 bg-blue-50 px-5">
                    <span class="truncate text-sm font-medium text-blue-900">
                        Current: {{ currentLabel }}
                    </span>

                    <button v-if="hasCurrent && allowClear"
                            type="button"
                            class="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-blue-700 transition-colors duration-150 hover:text-blue-900"
                            @click="commit('')">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                        Clear
                    </button>
                </div>

                <!-- Empty state -->
                <div v-if="filtered.length === 0" class="flex flex-1 items-center justify-center px-5 py-10 text-center">
                    <div>
                        <p class="m-0 text-sm font-medium text-gray-900">No matches</p>
                        <p class="m-0 mt-0.5 text-sm text-gray-500">Try a different search term.</p>
                    </div>
                </div>

                <!-- Grouped (hierarchical) list -->
                <ul v-else-if="grouped" class="m-0 flex-1 list-none divide-y divide-gray-100 overflow-y-auto p-0">
                    <template v-for="(group, groupIdx) in groupedFiltered" :key="group.name">
                        <li :class="groupIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                            <div class="flex cursor-pointer select-none items-center gap-2 py-3 pl-4 pr-4 font-medium text-gray-900 hover:bg-gray-100"
                                 role="button"
                                 :aria-expanded="isExpanded(group.name)"
                                 @click="toggleExpanded(group.name)">
                                <span class="flex shrink-0 items-center justify-center text-gray-400">
                                    <svg v-if="isExpanded(group.name)" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6l6 -6"/></svg>
                                    <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6l-6 6"/></svg>
                                </span>
                                {{ group.name }}
                                <span class="text-xs font-normal text-gray-500">({{ group.options.length }})</span>
                                <span v-if="group.containsCurrent"
                                      class="ml-auto shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">current</span>
                            </div>
                        </li>

                        <template v-if="isExpanded(group.name)">
                            <li v-for="option in group.options"
                                :key="option.value"
                                :ref="el => setOptionEl(option.value, el)"
                                class="cursor-pointer py-2.5 pl-12 pr-4 text-sm"
                                :class="rowClass(option)"
                                @mouseenter="hoveredValue = option.value"
                                @mouseleave="hoveredValue = null"
                                @click="commit(option.value)">
                                <div class="flex items-center justify-between gap-3">
                                    <span class="min-w-0 flex-1">
                                        <span class="block truncate">{{ option.label }}</span>
                                        <alarm-threshold-chips v-if="hasAlarmData(option)" v-bind="chipProps(option)"></alarm-threshold-chips>
                                    </span>
                                    <span v-if="isCurrent(option)"
                                          class="shrink-0 text-xs text-blue-700">current</span>
                                </div>
                            </li>
                        </template>
                    </template>
                </ul>

                <!-- Flat list -->
                <ul v-else class="m-0 flex-1 list-none overflow-y-auto p-0">
                    <li v-for="option in filtered"
                        :key="option.value"
                        :ref="el => setOptionEl(option.value, el)"
                        class="cursor-pointer border-b border-gray-100 px-5 py-2.5 text-sm"
                        :class="rowClass(option)"
                        @mouseenter="hoveredValue = option.value"
                        @mouseleave="hoveredValue = null"
                        @click="commit(option.value)">
                        <div class="flex items-center justify-between gap-3">
                            <span class="min-w-0 flex-1">
                                <span class="block truncate">{{ option.label }}</span>
                                <alarm-threshold-chips v-if="hasAlarmData(option)" v-bind="chipProps(option)"></alarm-threshold-chips>
                            </span>
                            <span v-if="isCurrent(option)"
                                  class="shrink-0 text-xs text-blue-700">current</span>
                        </div>
                    </li>
                </ul>

                <div class="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
                    <span class="text-xs text-gray-500">{{ filtered.length }} of {{ options.length }}</span>

                    <div class="flex items-center gap-3">
                        <span class="hidden text-xs text-gray-500 sm:inline">Enter to select &middot; Esc to close</span>
                        <button type="button"
                                class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                @click="close">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
import AlarmThresholdChips from '@/components/Meters/AlarmThresholdChips.vue';

export default {
    name: 'QuickPickModal',
    components: {AlarmThresholdChips},
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            default: '',
        },
        subtitle: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: 'Search',
        },
        // [{value, label, group?}]. A `group` on any option turns the list into a tree.
        options: {
            type: Array,
            default: () => [],
        },
        currentValue: {
            type: [String, Number, null],
            default: null,
        },
        allowClear: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['update:open', 'select'],
    data() {
        return {
            query: '',
            // Tracked by value rather than index so highlighting survives collapsing a
            // group or re-filtering the list.
            highlightedValue: null,
            // Hover is tracked apart from the keyboard cursor so moving the mouse away restores
            // the selected row's marker instead of leaving the last hovered row lit.
            hoveredValue: null,
            expanded: {},
            optionEls: {},
        };
    },
    computed: {
        grouped() {
            return this.options.some(o => !! o.group);
        },
        filtered() {
            const q = this.query.trim().toLowerCase();
            if (! q) {
                return this.options;
            }

            // Threshold values are searchable too, so typing "36" finds the profile that uses it.
            return this.options.filter(o => {
                const thresholds = [o.high, o.low].filter(v => v !== null && v !== undefined).join(' ');

                return `${o.group ?? ''} ${o.label} ${thresholds}`.toLowerCase().includes(q);
            });
        },
        // Filtered options bucketed by their group, preserving option order.
        groupedFiltered() {
            const groups = [];
            const index = {};

            this.filtered.forEach(option => {
                const name = option.group ?? '';
                if (! (name in index)) {
                    index[name] = groups.length;
                    groups.push({name, options: [], containsCurrent: false});
                }

                const group = groups[index[name]];
                group.options.push(option);
                if (this.isCurrent(option)) {
                    group.containsCurrent = true;
                }
            });

            return groups;
        },
        // Options in the order they are actually rendered, so arrow keys follow the
        // visible list and skip anything inside a collapsed group.
        navigableOptions() {
            if (! this.grouped) {
                return this.filtered;
            }

            return this.groupedFiltered.flatMap(g => this.isExpanded(g.name) ? g.options : []);
        },
        allExpanded() {
            return this.groupedFiltered.every(g => this.isExpanded(g.name));
        },
        normalizedCurrent() {
            return this.currentValue === null || this.currentValue === undefined ? '' : String(this.currentValue);
        },
        // The banner is only meaningful when this picker is editing a single field that has a
        // value today. A bulk apply spans points with differing values, so it is hidden.
        showCurrent() {
            return this.hasCurrent;
        },
        hasCurrent() {
            return this.normalizedCurrent !== '';
        },
        currentLabel() {
            const option = this.options.find(o => o.value === this.normalizedCurrent);
            if (! option) {
                return this.normalizedCurrent;
            }

            return option.group ? `${option.group} / ${option.label}` : option.label;
        },
    },
    watch: {
        open(isOpen) {
            if (! isOpen) {
                return;
            }

            this.query = '';
            this.hoveredValue = null;
            this.optionEls = {};

            // Open with the current value's group expanded and the rest collapsed, so a
            // site with many areas opens scannable rather than as one long list.
            const current = this.options.find(o => o.value === this.normalizedCurrent);
            if (this.grouped) {
                const expanded = {};
                this.options.forEach(o => {
                    if (o.group) {
                        expanded[o.group] = current ? o.group === current.group : true;
                    }
                });
                this.expanded = expanded;
            }

            // Start on the current value so Enter without typing changes nothing.
            this.highlightedValue = current ? current.value : (this.navigableOptions[0]?.value ?? null);

            this.$nextTick(() => {
                this.$refs.search?.focus();
                this.scrollHighlightedIntoView();
            });
        },
        query(value) {
            // Searching should reach into collapsed groups rather than hide matches.
            if (this.grouped && value.trim()) {
                const expanded = {};
                this.groupedFiltered.forEach(g => {
                    expanded[g.name] = true;
                });
                this.expanded = expanded;
            }

            this.highlightedValue = this.navigableOptions[0]?.value ?? null;
        },
    },
    methods: {
        setOptionEl(value, el) {
            if (el) {
                this.optionEls[value] = el;
            }
        },
        // Zone and scaling-profile options carry no alarm data, so the chips must not render for
        // them -- without this a zone reads as "Zone 1 Thr Off".
        hasAlarmData(option) {
            return option.high !== undefined
                || option.low !== undefined
                || option.threshold_alarms_enabled !== undefined;
        },

        // Mirrors the grid's mapping so an option looks the same in both places.
        chipProps(option) {
            return {
                high: option.high ?? null,
                low: option.low ?? null,
                thresholdAlarms: !! option.threshold_alarms_enabled,
                thresholdResolve: !! option.threshold_resolve_enabled,
                offlineAlarms: !! option.offline_alarms_enabled,
                offlineResolve: !! option.offline_resolve_enabled,
                hasAnyThreshold: !! option.has_any_threshold,
            };
        },

        isCurrent(option) {
            return option.value === this.normalizedCurrent;
        },
        isHighlighted(option) {
            return option.value === this.highlightedValue;
        },
        // Three distinct states, in priority order:
        //   hover      - transient, follows the mouse and clears on mouseleave
        //   keyboard   - the arrow-key cursor
        //   current    - the value the field holds today, always marked so it stays findable
        //
        // Hover and the keyboard cursor are both grey rather than blue. Alarm profile rows carry
        // threshold chips with their own fixed backgrounds (red-100 high, blue-100 low), so a blue
        // row tint rendered the low chip blue-on-blue and made it disappear under the cursor; a
        // solid blue cursor did the same to both chips. Grey stays neutral behind either chip.
        rowClass(option) {
            if (this.hoveredValue === option.value) {
                return 'bg-gray-100 text-gray-900';
            }

            if (this.isHighlighted(option)) {
                return 'bg-gray-200 text-gray-900';
            }

            return this.isCurrent(option)
                ? 'bg-blue-50 font-medium text-blue-900'
                : 'bg-white text-gray-700';
        },
        isExpanded(groupName) {
            return this.expanded[groupName] !== false;
        },
        toggleExpanded(groupName) {
            this.expanded = {...this.expanded, [groupName]: ! this.isExpanded(groupName)};
        },
        toggleAllExpanded() {
            const target = ! this.allExpanded;
            const expanded = {};
            this.groupedFiltered.forEach(g => {
                expanded[g.name] = target;
            });
            this.expanded = expanded;
        },
        move(delta) {
            const list = this.navigableOptions;
            if (list.length === 0) {
                return;
            }

            const current = list.findIndex(o => o.value === this.highlightedValue);
            const next = (current + delta + list.length) % list.length;
            this.highlightedValue = list[next].value;
            this.scrollHighlightedIntoView();
        },
        scrollHighlightedIntoView() {
            this.$nextTick(() => {
                this.optionEls[this.highlightedValue]?.scrollIntoView({block: 'nearest'});
            });
        },
        commitHighlighted() {
            if (this.highlightedValue !== null) {
                this.commit(this.highlightedValue);
            }
        },
        commit(value) {
            this.$emit('select', value);
            this.close();
        },
        close() {
            this.$emit('update:open', false);
        },
    },
};
</script>
